import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
    query,
    setSearchQuery,
    handleKeyDown,
    handleClickSearch,
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';
import { debounce } from 'lodash-es';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);

const QuickSearch = () => {
    const {
        query,
        result,
        showResult,
        selectedItem,
    } = useSelector((state) => state.quickSearch);
    const [lastClickedNode, setLastClickedNode] = useState(null);
    const [showResultList, setShowResultList] = useState(false);
    const searchUrl =
        window.__litium.quickSearchUrl +
        (query.length > 0 ? `?q=${query}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const previousSelectedItem = usePrevious(selectedItem);

    const clickHandler = useCallback((event) => {
        setLastClickedNode(event.target);
    }, []);
    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        // set query value if it is avaialble in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem !== previousSelectedItem) {
            const el = document.querySelector(
                '.quick-search-result__item--selected'
            );
            el &&
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
        }
    }, [selectedItem, previousSelectedItem]);

    useEffect(() => {
        const input = searchInput.current;
        if (!input) return;

        const basePlaceholder = input.getAttribute('placeholder')?.trim() || '';
        const rawPhrases = input.dataset.searchPhrases || '';

        const phrases = rawPhrases
            .split(',')
            .map(p => p.replace(/^"|"$/g, '').trim())
            .filter(Boolean);

        if (!phrases.length) return;

        let wordIndex = 0;
        let charIndex = 0;
        let isDeleting = false;
        let timeoutId = null;

        const settings = {
            typingSpeed: 90,
            deletingSpeed: 45,
            wordPause: 1200,
            nextWordPause: 500,
        };

        const resetTypewriter = () => {
            wordIndex = 0;
            charIndex = 0;
            isDeleting = false;
            clearTimeout(timeoutId);
            input.placeholder = basePlaceholder; // start placeholder fr�n scratch
        };

        const updatePlaceholder = () => {
            const word = phrases[wordIndex];
            input.placeholder = `${basePlaceholder} ${word.substring(0, charIndex)}`;
        };

        const typeWriter = () => {
            // Pausa om anv�ndaren skriver n�got
            if (input.value) return;

            const currentWord = phrases[wordIndex];

            if (!isDeleting) {
                charIndex++;
                updatePlaceholder();

                if (charIndex === currentWord.length) {
                    timeoutId = setTimeout(() => {
                        isDeleting = true;
                    }, settings.wordPause);
                }
            } else {
                charIndex--;
                updatePlaceholder();

                if (charIndex === 0) {
                    isDeleting = false;
                    wordIndex = (wordIndex + 1) % phrases.length;
                    timeoutId = setTimeout(() => { }, settings.nextWordPause);
                }
            }

            timeoutId = setTimeout(
                typeWriter,
                isDeleting ? settings.deletingSpeed : settings.typingSpeed
            );
        };

        const pause = () => clearTimeout(timeoutId);

        const resume = () => {
            if (!input.value) {
                resetTypewriter();
                typeWriter();
            }
        };

        // Event listeners
        input.addEventListener('focus', pause);
        input.addEventListener('input', pause);
        input.addEventListener('blur', resume);

        // Starta typewriter p� load
        typeWriter();

        return () => {
            clearTimeout(timeoutId);
            input.removeEventListener('focus', pause);
            input.removeEventListener('input', pause);
            input.removeEventListener('blur', resume);
        };
    }, []);

    const handleShow = (value) => {
        if (value) {
            setShowResultList(true);
        } else {
            setShowResultList(false);
        }
    }

    return (
        <div className="quick-search__box-wrapper">
            <div className="quick-search__box" role="search" ref={searchContainer}>
                <div
                    className={'quick-search__form'}
                    role="search"
                >
                    <div className="quick-search__input-wrapper">
                        <input
                            className="quick-search__input"
                            type="search"
                            placeholder={translate('quicksearch.placeholder')}
                            data-search-phrases={translate('quicksearch.phrases')}
                            autoComplete="off"
                            value={decodeURIComponent(query)}
                            onChange={(event) => {
                                const text = encodeURIComponent(event.target.value);
                                dispatch(setSearchQuery(text));
                                debouncedQuery(dispatch, text);
                                handleShow(event.target.value);
                            }}
                            onKeyDown={(event) =>
                                dispatch(handleKeyDown(event, { searchUrl }))
                            }
                            ref={searchInput}
                            onBlur={() => {
                                if (
                                    searchContainer.current &&
                                    !searchContainer.current.contains(lastClickedNode)
                                ) {
                                    setShowResultList(false);
                                }
                            }}
                            onMouseDown={() => setShowResultList(true)}
                        />
                        <button
                            className="quick-search__submit-button"
                            type="button"
                            aria-label={translate('code.header.search.button.title')}
                            onClick={(e) => {
                                e.preventDefault();
                                dispatch(handleClickSearch({ searchUrl }));
                            }}
                        >
                            <i className="quick-search__submit-icon"></i>                    
                        </button>
                    </div>
                    {showResult && showResultList && (
                        <QuickSearchResult
                            result={result}
                            selectedItem={selectedItem}
                            searchUrl={searchUrl}
                        />
                    )}
            </div>
            </div>
        </div>
    );
};

export default QuickSearch;
